import { spacing } from '@scality/core-ui';
import styled, { css } from 'styled-components';

/**
 * The look of a sidebar list row, shared by the conversation history and the dashboard
 * list. Only the styling is shared: the two rows differ in behavior (a conversation row
 * selects a conversation and carries an export button, a dashboard row is a route link),
 * so each keeps its own element and semantics.
 *
 * Hover previews the selected background; the marker bar alone means "this is the one", so
 * hovering the selected row changes nothing. Focus is kept off hover to stay visible there.
 */
export const sidebarRowStyles = css`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${spacing.r8};
  /* Tall enough for the conversation row's Copy button (r32), so both lists align. */
  min-height: ${spacing.r32};
  padding: ${spacing.r8} ${spacing.r12};
  font-size: 0.85rem;
  text-decoration: none;
  color: ${({ theme }) => theme.textSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.backgroundLevel3};

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    background-color: ${({ theme }) => theme.chatCardBlue};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.infoPrimary};
    outline-offset: -2px;
  }
`;

/** The row's current-item state: applied on `.active` (link) or on a selected flag. */
export const sidebarRowSelectedStyles = css`
  background-color: ${({ theme }) => theme.chatCardBlue};
  color: ${({ theme }) => theme.textPrimary};

  &::before {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    /* selectedActive (#037AFF), not infoPrimary — a grey-mauve that reads as disabled. */
    background-color: ${({ theme }) => theme.selectedActive};
  }
`;

/** Row label: takes the remaining width and ellipsizes instead of wrapping. */
export const SidebarRowLabel = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
